import { PropsOf } from "@emotion/react";
import { SPRING_ANIMATION } from "config/consts";
import { motion } from "framer-motion";
import {
  CSSProperties,
  PropsWithChildren,
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { isObject } from "lodash-es";

export interface AnimateChangeInHeightProps
  extends PropsWithChildren<React.HTMLProps<HTMLDivElement> & PropsOf<typeof motion.div>> {
  heightAddOn?: number;
  contentStyle?: CSSProperties;
  transition?: object;
}

// To allow exit animations to work, wrap this component as a direct child of
// <AnimatePresence>
export const AnimateChangeInHeight = forwardRef<HTMLDivElement, AnimateChangeInHeightProps>(
  ({ children, style, heightAddOn = 0, contentStyle, transition, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | "auto">(0);

    useLayoutEffect(() => {
      if (containerRef.current) {
        const resizeObserver = new ResizeObserver(entries => {
          // We only have one entry, so we can use entries[0].
          const observedHeight = entries[0].contentRect.height;
          setHeight(observedHeight + heightAddOn);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [heightAddOn]);

    return (
      <motion.div
        {...props}
        ref={ref as React.RefObject<HTMLDivElement>}
        style={{ height, ...style }}
        animate={isObject(props.animate) ? { height, ...props.animate } : { height }}
        transition={transition ?? SPRING_ANIMATION}
      >
        <div ref={containerRef} style={{ width: "100%", ...contentStyle }}>
          {children}
        </div>
      </motion.div>
    );
  }
);
